import { getStoreComparator } from "@/lib/storeSort";
import { useReportContext } from "@/stores/ReportContext";
import { useSessionData } from "@/stores/SessionDataContext";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
export default function StoreList() {
  const { stores, code_iata } = useSessionData();
  const { store } = useParams<{ store?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const storeId = store ? parseInt(store) : null;
  const { forced, reportId } = useReportContext();
  
  const basePath = location.pathname.startsWith("/market-demand") ? "/market-demand" : "";

  // One sorted list feeds both the dropdown and the tab strip below, so the two
  // stay consistent. Airports with a custom store order are handled in
  // storeSort; everything else gets the generic alphanumeric sort.
  const compareStores = getStoreComparator(code_iata);
  const sortedStores = [...stores].sort((a, b) =>
    compareStores(a.name, b.name)
  );
  const useDropdown = stores.length > 5;

  const getUrl = (path: string) => {
    if (forced) {
      return `${path}?report_id=${reportId}`;
    }
    return path;
  };

  return (
    <div className={"mx-4 md:mx-8 mt-4"}>
      <div
        className={classNames(
          useDropdown ? "w-full sm:w-auto" : "block sm:hidden",
          "block"
        )}
      >
        <label htmlFor="tabs" className="sr-only">
          Select a Store
        </label>
        <select
          id="tabs"
          name="tabs"
          // Width follows the longest store name rather than a fixed 24rem, so
          // the selected name is never clipped. min-w keeps the old size as the
          // floor for airports with short names; max-w stops one long name from
          // stretching the control across the page.
          className="block w-full max-w-full sm:w-auto sm:min-w-[24rem] sm:max-w-[52rem] rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-slate-900 dark:border-slate-600"
          value={storeId || "null"}
          onChange={(e) => {
            const val = e.target.value;
            navigate(getUrl(val === "null" ? (basePath || "/") : `${basePath}/store/${val}`));
          }}
        >
          <option value="null">All Stores</option>
          {sortedStores.map((tab) => (
            <option key={tab.key} value={tab.id}>
              {tab.name}
            </option>
          ))}
        </select>
      </div>
      <div className={classNames(useDropdown ? "hidden" : "hidden sm:block")}>
        <nav
          className="isolate flex divide-x divide-slate-600 rounded shadow overflow-x-auto scrollbar-thin scrollbar-thumb-slate-600"
          aria-label="Tabs"
        >
          <Link
            to={getUrl(basePath || "/")}
            className={classNames(
              storeId === null
                ? "text-gray-200"
                : "text-gray-400 hover:text-gray-700",
              "rounded-l-lg",
              "group relative shrink-0 bg-slate-800 py-3 px-6 text-center text-sm font-medium whitespace-nowrap hover:bg-indigo-900 hover:text-indigo-200 focus:z-10 cursor-pointer"
            )}
            aria-current={storeId === null ? "page" : undefined}
          >
            <span>All Stores</span>
            <span
              aria-hidden="true"
              className={classNames(
                storeId === null ? "bg-indigo-500" : "bg-transparent",
                "absolute inset-x-0 bottom-0 h-0.5"
              )}
            />
          </Link>
          {sortedStores.map((tab, tabIdx) => (
            <Link
              key={tab.name}
              to={getUrl(`${basePath}/store/${tab.id}`)}
              className={classNames(
                tab.id === storeId
                  ? "text-gray-200"
                  : "text-gray-400 hover:text-gray-700",
                tabIdx === sortedStores.length - 1 ? "rounded-r-lg" : "",
                "group relative shrink-0 bg-slate-800 py-3 px-6 text-center text-sm font-medium whitespace-nowrap hover:bg-indigo-900 hover:text-indigo-200 focus:z-10 cursor-pointer"
              )}
              aria-current={tab.id === storeId ? "page" : undefined}
            >
              <span>{tab.name}</span>
              <span
                aria-hidden="true"
                className={classNames(
                  tab.id === storeId ? "bg-indigo-500" : "bg-transparent",
                  "absolute inset-x-0 bottom-0 h-0.5"
                )}
              />
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
}
